import React, { useState, useMemo, useEffect, useLayoutEffect, useRef, useCallback } from "react"
import { EAppView } from "../../Enum/EAppViewType"
import MenuCard from "../Components/MenuCard"
import { OrderEntity } from "../../Objects/Order/OrderEntity"
import { EOrderPlaceType } from "../../Enum/EOrderPlaceType"
import LoginView from "./LoginView"
import PedidoView from "./OrderTrackingView"
import { SignupView } from "./SignupView"
import { ProfileView } from "./ProfileView"
import { OrderHistoryView } from "./OrderHistoryView"
import {
  addtionalMemory,
  awardMemory,
  categoryMemory,
  companyMemory,
  comboMemory,
  catalogItemsMemory,
  extraServiceMemory,
  productsMemory,
  orderMemory,
  promotionsMemory,
  noticeMemory,
} from "../../Objects/Memory/Memory"
import { ICartItem } from "../../Interfaces/ICardItem"
import { Navbar } from "../Components/Navbar"
import { AddProductRequestPupop } from "../Pupop/AddProductRequestPupop"
import { IProductRequest } from "../../Interfaces/IProductRequest"
import { IAddtionalRequest } from "../../Interfaces/IAddtionalRequest"
import { ISelectedSelectionRequest } from "../../Interfaces/ISelectionRequest"
import { OperatingScheduleEntity } from "../../Objects/OperatingSchedule/OperatingScheduleEntity"
import { PUBLIC_MENU_COMBO_CATEGORY_ID, buildAllProducts } from "../Helpers/BuildProductList"
import { ShoppingCartPupop } from "../Pupop/ShoppingCartPupop"
import { AuthState, usePublicViewContext } from "../../context/usePublicViewContext"
import { useReloadMemory } from "../../hooks/useReloadMemory"
import { useParams, useSearchParams } from "react-router-dom"
import { CategoryEntity } from "../../Objects/Category/CategoryEntity"
import { CalculateCartSummary } from "../Helpers/CalculateCartSummary"
import { ShoppingCartButton } from "../Components/ShoppingCartButton"
import { useTranslation } from "react-i18next"
import { SEO } from "../../seo/Seo"
import { buildStoreSchema } from "../../seo/schema"
import { EStoreServiceType } from "../../Enum/EStoreServiceType"
import InputField from "../../components/InputField/InputField"
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons"
import { filterMenuProductsByQuery } from "../Helpers/FilterMenuProducts"
import { upsertCartItem } from "../Helpers/CartItemHelpers"
import { SocialSharePupop } from "../Pupop/SocialSharePupop"
import { NoticeDetailsPupop } from "../Pupop/NoticeDetailsPupop"
import { NoticeEntity } from "../../Objects/Notice/NoticeEntity"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import { getPublicMenuTheme, isLegacyPublicMenuTheme } from "../Helpers/PublicMenuTheme"
import { EEnableStatusType } from "../../Enum/EEnableStatusType"
import { EGenderType } from "../../Enum/EGenderType"
import { EOldType } from "../../Enum/EOldType"
import { formatDateInTimeZone, getStoreNow } from "../../utils/timeZone"
import { OrderModeProximityHelper } from "../Helpers/OrderModeProximityHelper"
import { InstagramInAppBrowserNotice } from "../Components/InstagramInAppBrowserNotice"
import { useError } from "../../context/useErrorContext"
import { PublicMenuMarketingFooter } from "../Components/PublicMenuMarketingFooter"
import { MapPin } from "lucide-react"

const PUBLIC_MENU_PRODUCTS_PAGE_SIZE = 26

const isDarkHexColor = (hexColor: string) => {
  const normalizedColor = hexColor.replace("#", "").trim()

  if (normalizedColor.length !== 6) {
    return false
  }

  const red = Number.parseInt(normalizedColor.slice(0, 2), 16)

  const green = Number.parseInt(normalizedColor.slice(2, 4), 16)

  const blue = Number.parseInt(normalizedColor.slice(4, 6), 16)

  if ([red, green, blue].some(Number.isNaN)) {
    return false
  }

  return (red * 299 + green * 587 + blue * 114) / 1000 < 128
}

interface PublicMenuStartPageProps {
  servicePointSelfService?: { servicePointId: number; servicePointName: string; canCreateOrders: boolean }
  isCatalogLoading?: boolean
}

export const PublicMenuStartPage: React.FC<PublicMenuStartPageProps> = ({
  servicePointSelfService,
  isCatalogLoading = false,
}) => {
  const ALL_CATEGORY_ID = -1

  const AWARDS_TAB_ID = -2

  const PROMOTIONS_TAB_ID = -3

  const EXTRA_SERVICES_TAB_ID = -4

  const COMBO_CATEGORY_NAME = "combos"

  const PUBLIC_MENU_NAVBAR_STICKY_OFFSET_PX = 68

  const COMBO_CATEGORY_ICON = "🎁"

  const { storeSlug } = useParams<{ storeSlug: string }>()

  const [searchParams, setSearchParams] = useSearchParams()

  const { t } = useTranslation()

  const { auth, currentView, login, setCurrentView } = usePublicViewContext()

  const effectiveCustomer = servicePointSelfService ? null : auth.customer

  const { setError } = useError()

  const { handleReloadPublicOrder } = useReloadMemory()

  const [selectedNotice, setSelectedNotice] = useState<NoticeEntity | null>(null)

  const [activeCategory, setActiveCategory] = useState<number>(ALL_CATEGORY_ID)

  const [selectedProductForAddOns, setSelectedProductForAddOns] = useState<IProductRequest | null>(null)

  const [orderMode, setOrderMode] = useState<EOrderPlaceType>(
    servicePointSelfService ? EOrderPlaceType.Local : EOrderPlaceType.Delivery,
  )

  const [cart, setCart] = useState<ICartItem[]>([])

  const [isCartOpen, setIsCartOpen] = useState(false)

  const [currentSchedule, setCurrentSchedule] = useState<OperatingScheduleEntity>(null)

  const [isOpen, setIsOpen] = useState(false)

  const [isVacationActive, setIsVacationActive] = useState(false)

  const [allOrders, setAllOrders] = useState<OrderEntity[]>([])

  const [searchTerm, setSearchTerm] = useState("")

  const paginationKey = `${activeCategory}:${searchTerm}`

  const [productsPagination, setProductsPagination] = useState({
    key: paginationKey,
    page: 1,
  })

  const currentProductsPage = productsPagination.key === paginationKey ? productsPagination.page : 1

  const [isSocialOpen, setIsSocialOpen] = useState(false)

  const [signupCpfOrPhone, setSignupCpfOrPhone] = useState("")

  const hasInitializedViewFromUrl = useRef(false)

  const hydratedCartStorageKeyRef = useRef<string | null>(null)

  const categoryTabsContainerRef = useRef<HTMLDivElement | null>(null)

  const catalogContentRef = useRef<HTMLDivElement | null>(null)

  const categoryStickyBarRef = useRef<HTMLDivElement | null>(null)

  const categoryTabRefs = useRef<Record<number, HTMLButtonElement | null>>({})

  const categorySectionRefs = useRef<Record<number, HTMLElement | null>>({})

  const lastAutoScrolledCategoryIdRef = useRef<number | null>(null)

  const cartStorageKey = useMemo(
    () => `orderpro_public_cart:${storeSlug ?? "default"}:${orderMode}`,
    [storeSlug, orderMode],
  )

  const isValidAppView = (view: string | null): view is EAppView => {
    if (!view) {
      return false
    }

    if (servicePointSelfService) {
      return view === EAppView.CustomerMenu || view === EAppView.OrderTracking
    }

    return Object.values(EAppView).includes(view as EAppView)
  }

  const scrollCategoryTabIntoView = useCallback((categoryId: number) => {
    const tabsContainerElement = categoryTabsContainerRef.current

    const tabElement = categoryTabRefs.current[categoryId]

    if (!tabsContainerElement || !tabElement) {
      return
    }

    const tabsContainerRect = tabsContainerElement.getBoundingClientRect()

    const tabRect = tabElement.getBoundingClientRect()

    const nextScrollLeft =
      tabsContainerElement.scrollLeft +
      tabRect.left -
      tabsContainerRect.left -
      (tabsContainerRect.width - tabRect.width) / 2

    tabsContainerElement.scrollTo({ left: Math.max(0, nextScrollLeft), behavior: "smooth" })
  }, [])

  useEffect(() => {
    if (hasInitializedViewFromUrl.current) {
      return
    }

    const viewFromUrl = searchParams.get("view")

    if (isValidAppView(viewFromUrl) && viewFromUrl !== currentView) {
      setCurrentView(viewFromUrl)

      return
    }

    hasInitializedViewFromUrl.current = true
  }, [searchParams, currentView, setCurrentView])

  useEffect(() => {
    if (!hasInitializedViewFromUrl.current) {
      return
    }

    if (searchParams.get("view") === currentView) {
      return
    }

    const nextSearchParams = new URLSearchParams(searchParams)

    nextSearchParams.set("view", currentView)

    setSearchParams(nextSearchParams, { replace: true })
  }, [currentView, searchParams, setSearchParams])

  const allProducts = useMemo(() => {
    return buildAllProducts(auth.customer)
  }, [
    productsMemory.listData,
    catalogItemsMemory.listData,
    comboMemory.listData,
    addtionalMemory.listData,
    auth.customer,
  ])

  useEffect(() => {
    const loadOrders = async () => {
      if (currentView === EAppView.OrderHistory && auth.customer) {
        await handleReloadPublicOrder(storeSlug)

        setAllOrders(orderMemory.listData)
      }
    }

    loadOrders()
  }, [currentView])

  useEffect(() => {
    if (isCartOpen || selectedProductForAddOns) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = "unset"
    }
    return () => {
      document.body.style.overflow = "unset"
    }
  }, [isCartOpen, selectedProductForAddOns])

  useEffect(() => {
    const storedCart = localStorage.getItem(cartStorageKey)

    if (!storedCart) {
      setCart([])
      hydratedCartStorageKeyRef.current = cartStorageKey

      return
    }

    try {
      const parsedCart = JSON.parse(storedCart)

      if (Array.isArray(parsedCart)) {
        setCart(parsedCart)
      } else {
        setCart([])
      }
    } catch {
      setCart([])
    }

    hydratedCartStorageKeyRef.current = cartStorageKey
  }, [cartStorageKey])

  useEffect(() => {
    if (hydratedCartStorageKeyRef.current !== cartStorageKey) {
      return
    }

    if (cart.length === 0) {
      localStorage.removeItem(cartStorageKey)

      return
    }

    localStorage.setItem(cartStorageKey, JSON.stringify(cart))
  }, [cart, cartStorageKey])

  useEffect(() => {
    const now = getStoreNow(companyMemory.timeZoneId)
    const day = now.getDay()

    const schedule = companyMemory.operatingSchedules.find((e) => e.day === day) ?? null

    setCurrentSchedule(schedule)

    setIsOpen(schedule ? schedule.isOpen(now) : false)

    setIsVacationActive(companyMemory.isOnVacation(now))
  }, [])

  useEffect(() => {
    if (servicePointSelfService) {
      setOrderMode(EOrderPlaceType.Local)

      return
    }

    const resolveInitialOrderMode = async () => {
      const initialOrderMode = await OrderModeProximityHelper.resolveInitialOrderMode({
        storeServiceType: companyMemory.storeServiceType,
        storeLatitude: companyMemory.addressEntity?.latitude,
        storeLongitude: companyMemory.addressEntity?.longitude,
        localServiceRadiusMeters: companyMemory.localServiceRadiusMeters,
      })

      setOrderMode(initialOrderMode)
    }

    resolveInitialOrderMode()
  }, [servicePointSelfService])

  const handleLoginSuccess = (authState: AuthState) => {
    login(authState.token)

    setCurrentView(EAppView.CustomerMenu)
  }

  const onAddProduct = (product: IProductRequest) => {
    if (servicePointSelfService && !servicePointSelfService.canCreateOrders) {
      setError(t("general:label_title_erro"), t("servicePointSelfService:view_only"))

      return
    }

    setSelectedProductForAddOns(product)
  }

  const addToCart = (
    product: IProductRequest,
    selectedAddOns: IAddtionalRequest[],
    selectedSelections: ISelectedSelectionRequest[],
    quantity: number,
    notes: string,
    reservationInfo: {
      isReservation: boolean
      reservationDate: string
      reservationTime: string
    },
  ) => {
    const hasReservationInCart = cart.some((item) => item.isReservation)

    const hasNonReservationInCart = cart.some((item) => !item.isReservation)

    if (reservationInfo.isReservation && hasNonReservationInCart) {
      setError(
        t("general:label_title_erro"),
        "Você não pode misturar itens reservados com itens de pedido imediato no mesmo pedido.",
      )
      return
    }

    if (!reservationInfo.isReservation && hasReservationInCart) {
      setError(
        t("general:label_title_erro"),
        "Este pedido já possui itens de reserva. Adicione apenas itens reservados com mesmo dia e horário.",
      )
      return
    }

    if (reservationInfo.isReservation) {
      const cartReservation = cart.find((item) => item.isReservation)

      if (
        cartReservation &&
        (cartReservation.reservationDate !== reservationInfo.reservationDate ||
          cartReservation.reservationTime !== reservationInfo.reservationTime)
      ) {
        setError(
          t("general:label_title_erro"),
          "Todos os itens reservados no pedido precisam ter o mesmo dia e horário de reserva.",
        )
        return
      }
    }

    setCart((prev) =>
      upsertCartItem({
        currentCart: prev,
        product,
        selectedAddOns,
        selectedSelections,
        quantity,
        notes,
        isReservation: reservationInfo.isReservation,
        reservationDate: reservationInfo.reservationDate,
        reservationTime: reservationInfo.reservationTime,
      }),
    )

    setSelectedProductForAddOns(null)
  }

  const summary = useMemo(() => CalculateCartSummary(cart, effectiveCustomer), [cart, effectiveCustomer])

  const sortPromotionalFirst = (products: IProductRequest[]) => {
    const promotional: IProductRequest[] = []
    const regular: IProductRequest[] = []

    products.forEach((product) => {
      if ((product.oldPrice ?? 0) > product.price) {
        promotional.push(product)
        return
      }

      regular.push(product)
    })

    return [...promotional, ...regular]
  }

  const filteredProducts = useMemo(() => {
    let productsByCategory: IProductRequest[] = []

    if (activeCategory === ALL_CATEGORY_ID || searchTerm.length > 0) {
      productsByCategory = allProducts
    } else {
      productsByCategory = allProducts.filter((e) => e.categoryId === activeCategory)
    }

    return sortPromotionalFirst(filterMenuProductsByQuery(productsByCategory, searchTerm))
  }, [allProducts, activeCategory, searchTerm])

  const visibleProducts = useMemo(() => {
    const startIndex = (currentProductsPage - 1) * PUBLIC_MENU_PRODUCTS_PAGE_SIZE

    return filteredProducts.slice(startIndex, startIndex + PUBLIC_MENU_PRODUCTS_PAGE_SIZE)
  }, [currentProductsPage, filteredProducts])

  const totalProductsPages = Math.max(1, Math.ceil(filteredProducts.length / PUBLIC_MENU_PRODUCTS_PAGE_SIZE))

  useEffect(() => {
    setProductsPagination({ key: paginationKey, page: 1 })
  }, [isCatalogLoading, paginationKey])

  const changeProductsPage = (page: number) => {
    const nextPage = Math.min(Math.max(page, 1), totalProductsPages)

    if (nextPage === currentProductsPage) {
      return
    }

    setProductsPagination({ key: paginationKey, page: nextPage })

    requestAnimationFrame(() => {
      catalogContentRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
    })
  }

  const productsByCategoryForAllTab = useMemo(() => {
    const productsByCategoryId = new Map<number, IProductRequest[]>()

    visibleProducts.forEach((product) => {
      const categoryProducts = productsByCategoryId.get(product.categoryId)

      if (categoryProducts) {
        categoryProducts.push(product)
      } else {
        productsByCategoryId.set(product.categoryId, [product])
      }
    })

    const baseCategoryGroups = categoryMemory.listData.map((category) => ({
      category,
      products: productsByCategoryId.get(category.id) ?? [],
    }))

    const comboProducts = productsByCategoryId.get(PUBLIC_MENU_COMBO_CATEGORY_ID) ?? []

    if (comboProducts.length > 0) {
      const comboCategory = categoryMemory.listData.find(
        (category) => category.name.trim().toLowerCase() === COMBO_CATEGORY_NAME,
      )

      baseCategoryGroups.push({
        category: {
          id: comboCategory?.id ?? PUBLIC_MENU_COMBO_CATEGORY_ID,
          name: comboCategory?.name ?? "Combos",
          icon: comboCategory?.icon ?? COMBO_CATEGORY_ICON,
        } as CategoryEntity,
        products: comboProducts,
      })
    }

    return baseCategoryGroups.filter((categoryGroup) => categoryGroup.products.length > 0)
  }, [visibleProducts])

  useEffect(() => {
    if (currentView !== EAppView.CustomerMenu || activeCategory !== ALL_CATEGORY_ID) {
      lastAutoScrolledCategoryIdRef.current = null

      return
    }

    const scrollCurrentCategoryTabIntoView = () => {
      const categoriesList = productsByCategoryForAllTab

      if (categoriesList.length === 0) {
        return
      }

      const stickyHeight = categoryStickyBarRef.current?.offsetHeight ?? 0

      const categoryProbeY = PUBLIC_MENU_NAVBAR_STICKY_OFFSET_PX + stickyHeight + 16

      let currentCategoryId: number | null = null

      let nearestCategoryDistance = Number.POSITIVE_INFINITY

      categoriesList.forEach(({ category }) => {
        const sectionElement = categorySectionRefs.current[category.id]

        if (!sectionElement) {
          return
        }

        const sectionRect = sectionElement.getBoundingClientRect()

        if (sectionRect.top <= categoryProbeY && sectionRect.bottom >= categoryProbeY) {
          currentCategoryId = category.id

          nearestCategoryDistance = 0

          return
        }

        if (currentCategoryId !== null) {
          return
        }

        const categoryDistance = Math.abs(sectionRect.top - categoryProbeY)

        if (categoryDistance < nearestCategoryDistance) {
          currentCategoryId = category.id

          nearestCategoryDistance = categoryDistance
        }
      })

      if (currentCategoryId === null || lastAutoScrolledCategoryIdRef.current === currentCategoryId) {
        return
      }

      scrollCategoryTabIntoView(currentCategoryId)

      lastAutoScrolledCategoryIdRef.current = currentCategoryId
    }

    let animationFrameId = 0

    const scheduleScrollSync = () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId)
      }

      animationFrameId = requestAnimationFrame(scrollCurrentCategoryTabIntoView)
    }

    scrollCurrentCategoryTabIntoView()

    window.addEventListener("scroll", scheduleScrollSync, { passive: true })
    window.addEventListener("resize", scheduleScrollSync)

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId)
      }

      window.removeEventListener("scroll", scheduleScrollSync)
      window.removeEventListener("resize", scheduleScrollSync)
    }
  }, [activeCategory, currentView, productsByCategoryForAllTab, scrollCategoryTabIntoView])

  const validAwards = useMemo(
    () => awardMemory.listData.filter((award) => award.status === EEnableStatusType.Active),
    [awardMemory.listData],
  )

  const validPromotions = useMemo(() => {
    const now = getStoreNow(companyMemory.timeZoneId)
    return promotionsMemory.listData.filter((promotion) =>
      [EGenderType.All, EGenderType.Man, EGenderType.Woman].some((genderType) =>
        promotion.canApplyPromotion(now, genderType, EOldType.IsAdult),
      ),
    )
  }, [promotionsMemory.listData])

  const validExtraServices = useMemo(
    () => extraServiceMemory.listData.filter((service) => service.status === EEnableStatusType.Active),
    [extraServiceMemory.listData],
  )

  const validNotices = useMemo(() => {
    const now = getStoreNow(companyMemory.timeZoneId)

    return noticeMemory.listData.filter((notice) => notice.isVisibleAt(now))
  }, [noticeMemory.listData])

  type PublicMenuTab = { id: number; icon: string; name: string; type: "products" | "awards" | "promotions" | "extras" }

  const categoriesWithAllOption = useMemo(() => {
    const comboExists = allProducts.some((product) => product.categoryId === PUBLIC_MENU_COMBO_CATEGORY_ID)

    const validCategoryIds = new Set(
      allProducts
        .filter((product) => product.categoryId !== PUBLIC_MENU_COMBO_CATEGORY_ID)
        .map((product) => product.categoryId),
    )

    const productTabs: PublicMenuTab[] = [
      { id: ALL_CATEGORY_ID, icon: "🧾", name: t("publicMenu:home.all_categories"), type: "products" },
      ...categoryMemory.listData
        .filter((category) => validCategoryIds.has(category.id))
        .map((category) => ({ ...category, type: "products" as const })),
      ...(comboExists
        ? [
            {
              id: PUBLIC_MENU_COMBO_CATEGORY_ID,
              icon: COMBO_CATEGORY_ICON,
              name: COMBO_CATEGORY_NAME,
              type: "products" as const,
            },
          ]
        : []),
    ]

    const trailingInfoTabs: PublicMenuTab[] = []

    if (!servicePointSelfService && companyMemory.publicMenuShowAwardsTab) {
      trailingInfoTabs.push({
        id: AWARDS_TAB_ID,
        icon: "🏆",
        name: t("publicMenu:home.awards_tab"),
        type: "awards",
      })
    }

    if (companyMemory.publicMenuShowPromotionsTab) {
      trailingInfoTabs.push({
        id: PROMOTIONS_TAB_ID,
        icon: "🔥",
        name: t("publicMenu:home.promotions_tab"),
        type: "promotions",
      })
    }

    if (companyMemory.publicMenuShowExtraServicesTab) {
      trailingInfoTabs.push({
        id: EXTRA_SERVICES_TAB_ID,
        icon: "🧰",
        name: t("publicMenu:home.extra_services_tab"),
        type: "extras",
      })
    }

    return [...productTabs, ...trailingInfoTabs]
  }, [allProducts, categoryMemory.listData, t, servicePointSelfService])

  useEffect(() => {
    const hasActiveTab = categoriesWithAllOption.some((category) => category.id === activeCategory)

    if (!hasActiveTab) {
      setActiveCategory(ALL_CATEGORY_ID)
    }
  }, [categoriesWithAllOption, activeCategory])

  const publicMenuTheme = useMemo(() => getPublicMenuTheme(companyMemory), [companyMemory])

  const isLegacyTheme = isLegacyPublicMenuTheme(publicMenuTheme)

  useLayoutEffect(() => {
    const rootElement = document.getElementById("root")
    const themeColorMeta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')
    const appleStatusBarMeta = document.querySelector<HTMLMetaElement>(
      'meta[name="apple-mobile-web-app-status-bar-style"]',
    )
    const isDarkBackground = isDarkHexColor(publicMenuTheme.backgroundColor)

    const previousDocumentBackground = document.documentElement.style.background
    const previousDocumentBackgroundColor = document.documentElement.style.backgroundColor
    const previousDocumentColorScheme = document.documentElement.style.colorScheme
    const previousBodyBackground = document.body.style.background
    const previousBodyBackgroundColor = document.body.style.backgroundColor
    const previousBodyColor = document.body.style.color
    const previousBodyColorScheme = document.body.style.colorScheme
    const previousRootBackground = rootElement?.style.background
    const previousRootBackgroundColor = rootElement?.style.backgroundColor
    const previousRootMinHeight = rootElement?.style.minHeight
    const previousThemeColor = themeColorMeta?.getAttribute("content") ?? null
    const previousAppleStatusBarStyle = appleStatusBarMeta?.getAttribute("content") ?? null

    const forceMetaRefresh = (metaElement: HTMLMetaElement | null) => {
      if (!metaElement?.parentElement) {
        return
      }

      const parentElement = metaElement.parentElement

      parentElement.removeChild(metaElement)
      parentElement.appendChild(metaElement)
    }

    const applyThemeToDocument = () => {
      document.documentElement.style.background = publicMenuTheme.backgroundColor
      document.documentElement.style.backgroundColor = publicMenuTheme.backgroundColor
      document.documentElement.style.colorScheme = isDarkBackground ? "dark" : "light"
      document.body.style.background = publicMenuTheme.backgroundColor
      document.body.style.backgroundColor = publicMenuTheme.backgroundColor
      document.body.style.color = publicMenuTheme.textColor
      document.body.style.colorScheme = isDarkBackground ? "dark" : "light"

      if (rootElement) {
        rootElement.style.background = publicMenuTheme.backgroundColor
        rootElement.style.backgroundColor = publicMenuTheme.backgroundColor
        rootElement.style.minHeight = "100dvh"
      }

      themeColorMeta?.setAttribute("content", publicMenuTheme.backgroundColor)
      appleStatusBarMeta?.setAttribute("content", isDarkBackground ? "black-translucent" : "default")
      forceMetaRefresh(themeColorMeta)
      forceMetaRefresh(appleStatusBarMeta)
    }

    applyThemeToDocument()

    const animationFrameId = window.requestAnimationFrame(applyThemeToDocument)
    const timeoutIds = [50, 250, 1000].map((delay) => window.setTimeout(applyThemeToDocument, delay))
    const applyThemeWhenVisible = () => {
      if (!document.hidden) {
        applyThemeToDocument()
      }
    }

    window.addEventListener("pageshow", applyThemeToDocument)
    window.addEventListener("focus", applyThemeToDocument)
    document.addEventListener("visibilitychange", applyThemeWhenVisible)

    return () => {
      window.cancelAnimationFrame(animationFrameId)
      timeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId))
      window.removeEventListener("pageshow", applyThemeToDocument)
      window.removeEventListener("focus", applyThemeToDocument)
      document.removeEventListener("visibilitychange", applyThemeWhenVisible)

      document.documentElement.style.background = previousDocumentBackground
      document.documentElement.style.backgroundColor = previousDocumentBackgroundColor
      document.documentElement.style.colorScheme = previousDocumentColorScheme
      document.body.style.background = previousBodyBackground
      document.body.style.backgroundColor = previousBodyBackgroundColor
      document.body.style.color = previousBodyColor
      document.body.style.colorScheme = previousBodyColorScheme

      if (rootElement) {
        rootElement.style.background = previousRootBackground ?? ""
        rootElement.style.backgroundColor = previousRootBackgroundColor ?? ""
        rootElement.style.minHeight = previousRootMinHeight ?? ""
      }

      if (themeColorMeta) {
        if (previousThemeColor === null) {
          themeColorMeta.removeAttribute("content")
        } else {
          themeColorMeta.setAttribute("content", previousThemeColor)
        }
      }

      if (appleStatusBarMeta) {
        if (previousAppleStatusBarStyle === null) {
          appleStatusBarMeta.removeAttribute("content")
        } else {
          appleStatusBarMeta.setAttribute("content", previousAppleStatusBarStyle)
        }
      }
    }
  }, [publicMenuTheme.backgroundColor, publicMenuTheme.textColor])

  const storeName = companyMemory.companyName?.trim()

  const storeDescription = companyMemory.companyMessage?.trim()

  const storeLogo = companyMemory.iconUrl?.trim()

  const hasStoreIdentity = Boolean(storeName || storeDescription || storeLogo)

  const shouldShowDeliveryStatus = currentView === EAppView.CustomerMenu && orderMode === EOrderPlaceType.Delivery

  const shouldShowHeaderCard = hasStoreIdentity || shouldShowDeliveryStatus
  const isDeliveryAcceptingOrders = isOpen && !isVacationActive

  const deliveryScheduleText = currentSchedule
    ? isDeliveryAcceptingOrders
      ? t("publicMenu:home.open_until", { time: currentSchedule.closeTime.toShortTime() })
      : t("publicMenu:home.reopens_at", { time: currentSchedule.openTime.toShortTime() })
    : t("publicMenu:home.schedule_unavailable")

  const vacationMessage = t("publicMenu:home.vacation_message", {
    start: formatDateInTimeZone(companyMemory.vacationStartDate, "pt-BR", companyMemory.timeZoneId),
    end: formatDateInTimeZone(companyMemory.vacationEndDate, "pt-BR", companyMemory.timeZoneId),
  })

  const seoTitle = storeName
    ? t("publicMenu:home.seo_title_with_name", { name: storeName })
    : t("publicMenu:home.seo_title")

  const seoDescription = storeDescription || t("publicMenu:home.seo_description")

  return (
    <div
      className={`min-h-screen pb-24 font-sans selection:bg-orange-100 ${isLegacyTheme ? "bg-white" : ""}`}
      style={
        isLegacyTheme
          ? undefined
          : { backgroundColor: publicMenuTheme.backgroundColor, color: publicMenuTheme.textColor }
      }
    >
      <SEO
        title={seoTitle}
        description={seoDescription}
        path={`/${storeSlug ?? ""}`}
        image={storeLogo || undefined}
        siteName={storeName || "catalojei"}
        noindex={Boolean(servicePointSelfService)}
        structuredData={buildStoreSchema(storeName || "Loja", `/${storeSlug ?? ""}`, storeDescription, storeLogo)}
      />

      {servicePointSelfService ? (
        <header
          className={`sticky top-0 z-50 border-b px-4 py-3 shadow-sm ${isLegacyTheme ? "border-orange-200 bg-white" : ""}`}
          style={
            isLegacyTheme
              ? undefined
              : {
                  backgroundColor: publicMenuTheme.headerBackgroundColor,
                  borderColor: publicMenuTheme.borderColor,
                  color: publicMenuTheme.textColor,
                }
          }
        >
          <div className="mx-auto max-w-7xl">
            <div className="flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <div
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border shadow-sm"
                  style={{
                    backgroundColor: publicMenuTheme.loginButtonBackgroundColor,
                    color: publicMenuTheme.loginButtonTextColor,
                    borderColor: publicMenuTheme.loginButtonTextColor,
                  }}
                >
                  <MapPin size={22} />
                </div>

                <p
                  className="truncate text-lg font-black tracking-tight text-slate-900 sm:text-xl"
                  style={isLegacyTheme ? undefined : { color: publicMenuTheme.textColor }}
                >
                  {t("servicePointSelfService:service_point", { name: servicePointSelfService.servicePointName })}
                </p>
              </div>

              <button
                type="button"
                onClick={() =>
                  setCurrentView(
                    currentView === EAppView.OrderTracking ? EAppView.CustomerMenu : EAppView.OrderTracking,
                  )
                }
                className="rounded-lg bg-orange-600 px-4 py-2 text-xs font-black uppercase tracking-wider text-white"
                style={{
                  backgroundColor: publicMenuTheme.loginButtonBackgroundColor,
                  color: publicMenuTheme.loginButtonTextColor,
                  borderColor: publicMenuTheme.loginButtonTextColor,
                  borderWidth: publicMenuTheme.borderThickness,
                  boxShadow: `0 0 0 2px ${publicMenuTheme.loginButtonTextColor}`,
                }}

                  data-interaction-description-key="interactionDescriptions:publicMenu.pages.publicMenuStartPage.button.button">
                {currentView === EAppView.OrderTracking
                  ? t("publicMenu:navbar.menu")
                  : t("servicePointSelfService:consumption")}
              </button>
            </div>

            {!servicePointSelfService.canCreateOrders && currentView === EAppView.CustomerMenu && (
              <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-center text-xs font-bold text-amber-800">
                {t("servicePointSelfService:view_only")}
              </p>
            )}
          </div>
        </header>
      ) : (
        <Navbar
          setIsSocialOpen={setIsSocialOpen}
          currentView={currentView}
          setView={setCurrentView}
          mode={orderMode}
          theme={publicMenuTheme}
          storeServiceType={companyMemory.storeServiceType}
          onToggleMode={() => {
            if (companyMemory.storeServiceType !== EStoreServiceType.Both) {
              return
            }

            setOrderMode((e) => (e === EOrderPlaceType.Delivery ? EOrderPlaceType.Local : EOrderPlaceType.Delivery))

            setCurrentView(EAppView.CustomerMenu)

            setCart([])
          }}
        />
      )}

      <main className="max-w-7xl mx-auto px-1 mb:px-4 py-2">
        {!servicePointSelfService && <InstagramInAppBrowserNotice />}

        <Animate type={AnimationTypes.fadeInTraslate}>
          {shouldShowHeaderCard && (
            <section
              className={`mb-2 rounded-lg px-3 py-2 shadow-sm animate-in fade-in duration-300 ${isLegacyTheme ? "border border-gray-100 bg-white" : "border"}`}
              style={
                isLegacyTheme
                  ? undefined
                  : {
                      borderColor: publicMenuTheme.borderColor,
                      borderWidth: publicMenuTheme.borderThickness,
                      background: publicMenuTheme.useGradientHeader
                        ? `linear-gradient(135deg, ${publicMenuTheme.headerBackgroundColor} 0%, ${publicMenuTheme.surfaceColor} 55%, ${publicMenuTheme.backgroundColor} 100%)`
                        : publicMenuTheme.headerBackgroundColor,
                    }
              }
            >
              <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                {hasStoreIdentity && (
                  <Animate type={AnimationTypes.fadeInTraslate} duration="1s">
                    <div className="flex min-w-0 items-center gap-2.5">
                      {storeLogo && (
                        <img
                          src={storeLogo.toClientRequest()}
                          alt={storeName || "Logo da loja"}
                          className="h-10 w-10 shrink-0 rounded-md border border-gray-100 bg-white object-cover"
                          style={isLegacyTheme ? undefined : { borderColor: publicMenuTheme.borderColor }}
                          loading="lazy"
                          decoding="async"
                        />
                      )}

                      <div className="min-w-0">
                        {storeName && (
                          <h1
                            className="truncate text-xs sm:text-sm font-black text-gray-950 uppercase tracking-wide"
                            style={isLegacyTheme ? undefined : { color: publicMenuTheme.storeNameColor }}
                          >
                            {storeName}
                          </h1>
                        )}

                        {storeName && (
                          <p
                            className="truncate text-[10px] sm:text-[11px] text-gray-500"
                            style={isLegacyTheme ? undefined : { color: publicMenuTheme.storeDescriptionColor }}
                          >
                            {storeName}
                          </p>
                        )}
                      </div>
                    </div>
                  </Animate>
                )}

                {shouldShowDeliveryStatus && (
                  <div
                    className={`min-w-0 flex-1 sm:flex-none flex items-center gap-1.5 rounded-md border-2 px-2 py-1 sm:gap-2 sm:px-2.5 ${
                      isDeliveryAcceptingOrders ? "border-green-200 bg-green-50" : "border-gray-100 bg-red-50"
                    }`}
                    style={
                      isLegacyTheme
                        ? undefined
                        : isDeliveryAcceptingOrders
                          ? {
                              backgroundColor: publicMenuTheme.statusCardBackgroundColor,
                              borderColor: publicMenuTheme.buttonColor,
                            }
                          : {
                              borderColor: publicMenuTheme.borderColor,
                            }
                    }
                  >
                    <span className="shrink-0 text-[11px] leading-none">{isDeliveryAcceptingOrders ? "🛵" : "💤"}</span>

                    <p
                      className={`min-w-0 truncate text-[10px] font-bold sm:hidden ${isDeliveryAcceptingOrders ? "text-green-800" : "text-red-600"}`}
                    >
                      {isVacationActive
                        ? t("publicMenu:home.vacation_closed")
                        : isDeliveryAcceptingOrders
                          ? t("publicMenu:home.delivery_active")
                          : t("publicMenu:home.delivery_closed")}{" "}
                      • {isVacationActive ? vacationMessage : deliveryScheduleText}
                    </p>

                    <div className="hidden min-w-0 leading-tight sm:block">
                      <p
                        className={`truncate text-[10px] font-black uppercase ${isDeliveryAcceptingOrders ? "text-green-800" : "text-red-600"}`}
                      >
                        {isVacationActive
                          ? t("publicMenu:home.vacation_closed")
                          : isDeliveryAcceptingOrders
                            ? t("publicMenu:home.delivery_active")
                            : t("publicMenu:home.delivery_closed")}
                      </p>

                      <p
                        className={`truncate text-[10px] font-bold ${isDeliveryAcceptingOrders ? "text-green-700" : "text-red-600"}`}
                      >
                        {isVacationActive ? vacationMessage : deliveryScheduleText}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </section>
          )}
        </Animate>

        <section>
          {currentView === EAppView.LoginView && (
            <LoginView
              onTryLogin={handleLoginSuccess}
              onGoToSignup={(cpfOrPhone) => {
                setSignupCpfOrPhone(cpfOrPhone)
                setCurrentView(EAppView.SigupView)
              }}
            />
          )}

          {currentView === EAppView.SigupView && (
            <SignupView
              initialCpfOrPhone={signupCpfOrPhone}
              onSuccess={handleLoginSuccess}
              onGoToLogin={() => setCurrentView(EAppView.LoginView)}
            />
          )}

          {currentView === EAppView.ProfileView && <ProfileView />}

          {currentView === EAppView.CustomerMenu && (
            <>
              {validNotices.length > 0 && (
                <div className="space-y-2 p-2 pb-0">
                  {validNotices.map((notice) => (
                    <article
                      key={notice.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => setSelectedNotice(notice)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault()
                          setSelectedNotice(notice)
                        }
                      }}
                      className={`cursor-pointer rounded-lg border p-3 transition hover:opacity-90 ${isLegacyTheme ? "border-amber-200 border-2 bg-white" : ""}`}
                      style={
                        isLegacyTheme
                          ? undefined
                          : {
                              borderColor: publicMenuTheme.borderColor,
                              borderWidth: publicMenuTheme.borderThickness,
                              backgroundColor: publicMenuTheme.surfaceColor,
                              color: publicMenuTheme.textColor,
                            }
                      }
                      data-interaction-description-key="interactionDescriptions:publicMenu.pages.publicMenuStartPage.card.openNoticeDetails"
                    >
                      <div className="flex items-center gap-3">
                        {notice.iconUrl && (
                          <img
                            src={notice.iconUrl.toClientRequest()}
                            alt={notice.name}
                            className={`h-12 w-12 rounded-md object-cover ${isLegacyTheme ? "" : "border"}`}
                            style={
                              isLegacyTheme
                                ? undefined
                                : {
                                    borderColor: publicMenuTheme.borderColor,
                                    borderWidth: publicMenuTheme.borderThickness,
                                  }
                            }
                            loading="lazy"
                            decoding="async"
                          />
                        )}

                        <div className="min-w-0">
                          <p
                            className={`text-xs font-black uppercase tracking-wide ${isLegacyTheme ? "text-amber-700" : ""}`}
                            style={isLegacyTheme ? undefined : { color: publicMenuTheme.accentColor }}
                          >
                            {t("publicMenu:notice.label")}
                          </p>
                          <h3
                            className={`text-sm font-bold truncate ${isLegacyTheme ? "text-gray-900" : ""}`}
                            style={isLegacyTheme ? undefined : { color: publicMenuTheme.textColor }}
                          >
                            {notice.name}
                          </h3>
                          <p
                            className={`text-xs line-clamp-2 ${isLegacyTheme ? "text-gray-600" : ""}`}
                            style={isLegacyTheme ? undefined : { color: publicMenuTheme.textColor, opacity: 0.8 }}
                          >
                            {notice.description}
                          </p>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              )}

              <div
                ref={catalogContentRef}
                className="p-3"
                style={{ scrollMarginTop: PUBLIC_MENU_NAVBAR_STICKY_OFFSET_PX }}
              >
                <div
                  ref={categoryStickyBarRef}
                  className={`sticky z-40 -mx-3 mb-3 space-y-3 border-b px-3 pb-3 pt-2 shadow-sm ${
                    isLegacyTheme ? "border-gray-100 bg-white/95 backdrop-blur-xl" : "backdrop-blur-xl"
                  }`}
                  style={
                    isLegacyTheme
                      ? { top: PUBLIC_MENU_NAVBAR_STICKY_OFFSET_PX }
                      : {
                          top: PUBLIC_MENU_NAVBAR_STICKY_OFFSET_PX,
                          backgroundColor: `${publicMenuTheme.backgroundColor}F2`,
                          borderColor: publicMenuTheme.borderColor,
                        }
                  }
                >
                  <div
                    ref={categoryTabsContainerRef}
                    className="flex px-1 space-x-2 overflow-x-auto pb-2 no-scrollbar py-1"
                  >
                    {categoriesWithAllOption.map(
                      (category: CategoryEntity | { id: number; icon: string; name: string }) => (
                        <button
                          ref={(element) => {
                            categoryTabRefs.current[category.id] = element
                          }}
                          key={category.id}
                          onClick={() => {
                            setActiveCategory(category.id)
                            scrollCategoryTabIntoView(category.id)
                          }}
                          className={`flex-shrink-0 rounded-lg font-bold transition-all border ${
                            isLegacyTheme
                              ? activeCategory === category.id
                                ? "px-4 py-1 bg-white ring-2 ring-orange-300 text-black shadow-gray-200"
                                : "px-4 py-1 bg-gray-100 border-gray-100 text-gray-500 hover:bg-gray-50"
                              : ""
                          }`}
                          style={
                            isLegacyTheme
                              ? undefined
                              : {
                                  padding: publicMenuTheme.compactCategories ? "0.35rem 0.75rem" : "0.5rem 1rem",
                                  backgroundColor:
                                    activeCategory === category.id
                                      ? publicMenuTheme.primaryColor
                                      : publicMenuTheme.categoryBackgroundColor,
                                  borderColor:
                                    activeCategory === category.id
                                      ? publicMenuTheme.primaryColor
                                      : publicMenuTheme.categoryBorderColor,
                                  borderWidth: publicMenuTheme.borderThickness,
                                  color: activeCategory === category.id ? "#FFFFFF" : publicMenuTheme.textColor,
                                }
                          }

                  data-interaction-description-key="interactionDescriptions:publicMenu.pages.publicMenuStartPage.button.changeProductsPageCurrentProductsPage1">
                          {category.icon} <span className="ml-1">{category.name}</span>
                        </button>
                      ),
                    )}
                  </div>

                  <InputField
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={t("publicMenu:home.search_placeholder")}
                    icon={faMagnifyingGlass}
                    iconColor={isLegacyTheme ? undefined : publicMenuTheme.textColor}
                    containerStyle={
                      isLegacyTheme
                        ? undefined
                        : {
                            backgroundColor: publicMenuTheme.searchBackgroundColor,
                            borderColor: publicMenuTheme.searchBorderColor,
                            boxShadow: `0 0 0 2px ${publicMenuTheme.searchBorderColor}`,
                            color: publicMenuTheme.textColor,
                          }
                    }
                    inputStyle={isLegacyTheme ? undefined : { color: publicMenuTheme.textColor }}
                    placeholderBackgroundColor={isLegacyTheme ? undefined : publicMenuTheme.searchBackgroundColor}

                  descriptionKey="interactionDescriptions:publicMenu.pages.publicMenuStartPage.input.searchPlaceholder" />
                </div>

                {activeCategory === AWARDS_TAB_ID ? (
                  <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                    {validAwards.length === 0 ? (
                      <div className="text-center py-4 text-gray-400 font-semibold">
                        {t("publicMenu:home.no_valid_awards")}
                      </div>
                    ) : (
                      validAwards.map((award) => (
                        <article
                          key={award.id}
                          className={`rounded-2xl border p-4 shadow-sm transition-all hover:shadow-md ${isLegacyTheme ? "bg-white border-gray-200" : ""}`}
                          style={
                            isLegacyTheme
                              ? undefined
                              : {
                                  backgroundColor: publicMenuTheme.surfaceColor,
                                  borderColor: publicMenuTheme.borderColor,
                                  borderWidth: publicMenuTheme.borderThickness,
                                  color: publicMenuTheme.textColor,
                                }
                          }
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-3">
                              <div
                                className="flex h-10 w-10 items-center justify-center rounded-xl border text-lg"
                                style={
                                  isLegacyTheme
                                    ? undefined
                                    : {
                                        backgroundColor: publicMenuTheme.secondaryColor,
                                        borderColor: publicMenuTheme.borderColor,
                                        borderWidth: publicMenuTheme.borderThickness,
                                      }
                                }
                              >
                                🏆
                              </div>

                              <div>
                                <p
                                  className={`text-[10px] font-black uppercase tracking-widest ${isLegacyTheme ? "text-orange-700" : ""}`}
                                  style={isLegacyTheme ? undefined : { color: publicMenuTheme.accentColor }}
                                >
                                  {t("publicMenu:home.valid_now")}
                                </p>
                                <h3
                                  className={`mt-0.5 text-sm font-black ${isLegacyTheme ? "text-slate-800" : ""}`}
                                  style={isLegacyTheme ? undefined : { color: publicMenuTheme.textColor }}
                                >
                                  {award.name}
                                </h3>
                              </div>
                            </div>

                            <span
                              className="rounded-full px-2 py-1 text-[10px] font-black uppercase tracking-wider"
                              style={
                                isLegacyTheme
                                  ? undefined
                                  : {
                                      backgroundColor: publicMenuTheme.secondaryColor,
                                      color: publicMenuTheme.accentColor,
                                    }
                              }
                            >
                              {t("publicMenu:home.loyalty_badge")}
                            </span>
                          </div>
                          {award.description && (
                            <p
                              className={`mt-3 text-xs ${isLegacyTheme ? "text-slate-600" : ""}`}
                              style={isLegacyTheme ? undefined : { color: publicMenuTheme.textColor, opacity: 0.8 }}
                            >
                              {award.description}
                            </p>
                          )}
                          <p
                            className={`mt-3 rounded-xl px-3 py-2 text-xs font-bold ${isLegacyTheme ? "text-orange-700 bg-orange-50" : ""}`}
                            style={
                              isLegacyTheme
                                ? undefined
                                : {
                                    color: publicMenuTheme.accentColor,
                                    backgroundColor: publicMenuTheme.secondaryColor,
                                  }
                            }
                          >
                            {t("publicMenu:home.award_rule", {
                              count: award.countToWin,
                              minimum: award.minimumValue.toMoney(),
                            })}
                          </p>
                        </article>
                      ))
                    )}
                  </div>
                ) : activeCategory === PROMOTIONS_TAB_ID ? (
                  <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                    {validPromotions.length === 0 ? (
                      <div className="text-center py-4 text-gray-400 font-semibold">
                        {t("publicMenu:home.no_valid_promotions")}
                      </div>
                    ) : (
                      validPromotions.map((promotion) => (
                        <article
                          key={promotion.id}
                          className={`rounded-2xl border p-4 shadow-sm transition-all hover:shadow-md ${isLegacyTheme ? "bg-white border-gray-200" : ""}`}
                          style={
                            isLegacyTheme
                              ? undefined
                              : {
                                  backgroundColor: publicMenuTheme.surfaceColor,
                                  borderColor: publicMenuTheme.borderColor,
                                  borderWidth: publicMenuTheme.borderThickness,
                                  color: publicMenuTheme.textColor,
                                }
                          }
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-3">
                              <div
                                className="flex h-10 w-10 items-center justify-center rounded-xl border text-lg"
                                style={
                                  isLegacyTheme
                                    ? undefined
                                    : {
                                        backgroundColor: publicMenuTheme.secondaryColor,
                                        borderColor: publicMenuTheme.borderColor,
                                        borderWidth: publicMenuTheme.borderThickness,
                                      }
                                }
                              >
                                🔥
                              </div>

                              <div>
                                <p
                                  className={`text-[10px] font-black uppercase tracking-widest ${isLegacyTheme ? "text-orange-700" : ""}`}
                                  style={isLegacyTheme ? undefined : { color: publicMenuTheme.accentColor }}
                                >
                                  {t("publicMenu:home.valid_now")}
                                </p>
                                <h3
                                  className={`mt-0.5 text-sm font-black ${isLegacyTheme ? "text-slate-800" : ""}`}
                                  style={isLegacyTheme ? undefined : { color: publicMenuTheme.textColor }}
                                >
                                  {promotion.name}
                                </h3>
                              </div>
                            </div>

                            <span
                              className="rounded-full px-2 py-1 text-[10px] font-black uppercase tracking-wider"
                              style={
                                isLegacyTheme
                                  ? undefined
                                  : {
                                      backgroundColor: publicMenuTheme.secondaryColor,
                                      color: publicMenuTheme.accentColor,
                                    }
                              }
                            >
                              {t("publicMenu:home.offer_badge")}
                            </span>
                          </div>
                          {promotion.description && (
                            <p
                              className={`mt-3 text-xs ${isLegacyTheme ? "text-slate-600" : ""}`}
                              style={isLegacyTheme ? undefined : { color: publicMenuTheme.textColor, opacity: 0.8 }}
                            >
                              {promotion.description}
                            </p>
                          )}
                          <p
                            className={`mt-3 rounded-xl px-3 py-2 text-xs font-bold ${isLegacyTheme ? "text-orange-700 bg-orange-50" : ""}`}
                            style={
                              isLegacyTheme
                                ? undefined
                                : {
                                    color: publicMenuTheme.accentColor,
                                    backgroundColor: publicMenuTheme.secondaryColor,
                                  }
                            }
                          >
                            {t("publicMenu:home.promotion_discount_value", {
                              value: promotion.getValueToStringMoney(),
                            })}
                          </p>
                        </article>
                      ))
                    )}
                  </div>
                ) : activeCategory === EXTRA_SERVICES_TAB_ID ? (
                  <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                    {validExtraServices.length === 0 ? (
                      <div className="text-center py-4 text-gray-400 font-semibold">
                        {t("publicMenu:home.no_valid_extra_services")}
                      </div>
                    ) : (
                      validExtraServices.map((service) => (
                        <article
                          key={service.id}
                          className={`rounded-2xl border p-4 shadow-sm transition-all hover:shadow-md ${isLegacyTheme ? "bg-white border-gray-200" : ""}`}
                          style={
                            isLegacyTheme
                              ? undefined
                              : {
                                  backgroundColor: publicMenuTheme.surfaceColor,
                                  borderColor: publicMenuTheme.borderColor,
                                  borderWidth: publicMenuTheme.borderThickness,
                                  color: publicMenuTheme.textColor,
                                }
                          }
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-3">
                              <div
                                className="flex h-10 w-10 items-center justify-center rounded-xl border text-lg"
                                style={
                                  isLegacyTheme
                                    ? undefined
                                    : {
                                        backgroundColor: publicMenuTheme.secondaryColor,
                                        borderColor: publicMenuTheme.borderColor,
                                        borderWidth: publicMenuTheme.borderThickness,
                                      }
                                }
                              >
                                🧰
                              </div>

                              <div>
                                <p
                                  className={`text-[10px] font-black uppercase tracking-widest ${isLegacyTheme ? "text-orange-700" : ""}`}
                                  style={isLegacyTheme ? undefined : { color: publicMenuTheme.accentColor }}
                                >
                                  {t("publicMenu:home.valid_now")}
                                </p>
                                <h3
                                  className={`mt-0.5 text-sm font-black ${isLegacyTheme ? "text-slate-800" : ""}`}
                                  style={isLegacyTheme ? undefined : { color: publicMenuTheme.textColor }}
                                >
                                  {service.name}
                                </h3>
                              </div>
                            </div>

                            <span
                              className="rounded-full px-2 py-1 text-[10px] font-black uppercase tracking-wider"
                              style={
                                isLegacyTheme
                                  ? undefined
                                  : {
                                      backgroundColor: publicMenuTheme.secondaryColor,
                                      color: publicMenuTheme.accentColor,
                                    }
                              }
                            >
                              Extra
                            </span>
                          </div>
                          {service.description && (
                            <p
                              className={`mt-3 text-xs ${isLegacyTheme ? "text-slate-600" : ""}`}
                              style={isLegacyTheme ? undefined : { color: publicMenuTheme.textColor, opacity: 0.8 }}
                            >
                              {service.description}
                            </p>
                          )}
                          <p
                            className={`mt-3 rounded-xl px-3 py-2 text-xs font-bold ${isLegacyTheme ? "text-orange-700 bg-orange-50" : ""}`}
                            style={
                              isLegacyTheme
                                ? undefined
                                : {
                                    color: publicMenuTheme.accentColor,
                                    backgroundColor: publicMenuTheme.secondaryColor,
                                  }
                            }
                          >
                            {t("publicMenu:home.extra_service_value", "Valor: {{value}}", {
                              value: service.getAddValue().toMoney(),
                            })}
                          </p>
                        </article>
                      ))
                    )}
                  </div>
                ) : isCatalogLoading ? (
                  <div className="flex flex-col items-center justify-center gap-3 py-12 text-gray-500">
                    <div className="h-9 w-9 animate-spin rounded-full border-4 border-orange-600 border-t-transparent" />
                    <p className="text-sm font-bold">{t("publicMenu:home.loading_catalog")}</p>
                  </div>
                ) : activeCategory === ALL_CATEGORY_ID ? (
                  <Animate
                    key={`products-${paginationKey}-${currentProductsPage}`}
                    type={AnimationTypes.fadeInTraslate}
                    duration="1.2s"
                  >
                    <div className="space-y-4">
                      {productsByCategoryForAllTab.length === 0 ? (
                        <div className="col-span-full text-center py-4 text-gray-400 font-semibold">
                          {searchTerm.trim()
                            ? t("publicMenu:home.no_products_found")
                            : t("publicMenu:home.no_products_in_category")}
                        </div>
                      ) : (
                        productsByCategoryForAllTab.map(({ category, products }) => (
                          <section
                            ref={(element) => {
                              categorySectionRefs.current[category.id] = element
                            }}
                            key={category.id}
                            className="space-y-2"
                          >
                            <div className="flex items-center gap-2 px-1 pt-1">
                              <span
                                className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-widest ${
                                  isLegacyTheme ? "text-gray-700 border border-gray-300" : ""
                                }`}
                                style={
                                  isLegacyTheme
                                    ? undefined
                                    : {
                                        color: publicMenuTheme.textColor,
                                        borderColor: publicMenuTheme.categoryBorderColor,
                                        backgroundColor: publicMenuTheme.categoryBackgroundColor,
                                        borderWidth: publicMenuTheme.borderThickness,
                                      }
                                }
                              >
                                <span>{category.icon}</span>
                                <span>{category.name}</span>
                              </span>

                              <div
                                className={`h-px flex-1 ${isLegacyTheme ? "bg-gray-200" : ""}`}
                                style={
                                  isLegacyTheme
                                    ? undefined
                                    : {
                                        background: `linear-gradient(to right, ${publicMenuTheme.categoryDividerColor}, transparent)`,
                                      }
                                }
                              />
                            </div>

                            <div
                              className={`grid grid-cols-1 ${publicMenuTheme.cardLayout === "cover" ? (isLegacyTheme ? "sm:grid-cols-2 lg:grid-cols-4 gap-4" : "sm:grid-cols-2 lg:grid-cols-4 gap-4") : "gap-2 md:grid-cols-3"}`}
                            >
                              {products.map((product) => (
                                <MenuCard
                                  key={`${category.id}-${product.id}`}
                                  product={product}
                                  onAddToCart={onAddProduct}
                                  onViewDetails={onAddProduct}
                                  mode={orderMode}
                                  isOpen={!!servicePointSelfService || isDeliveryAcceptingOrders}
                                  theme={publicMenuTheme}
                                  canOrderDirectly={
                                    servicePointSelfService?.canCreateOrders ?? orderMode === EOrderPlaceType.Delivery
                                  }
                                />
                              ))}
                            </div>
                          </section>
                        ))
                      )}
                    </div>
                  </Animate>
                ) : (
                  <Animate
                    key={`products-${paginationKey}-${currentProductsPage}`}
                    type={AnimationTypes.fadeInTraslate}
                    duration="1.2s"
                  >
                    <div
                      className={`grid grid-cols-1 ${publicMenuTheme.cardLayout === "cover" ? (isLegacyTheme ? "sm:grid-cols-2 lg:grid-cols-4 gap-4" : "sm:grid-cols-2 lg:grid-cols-4 gap-4") : "gap-2 md:grid-cols-3"}`}
                    >
                      {filteredProducts.length === 0 ? (
                        <div className="col-span-full text-center py-4 text-gray-400 font-semibold">
                          {searchTerm.trim()
                            ? t("publicMenu:home.no_products_found")
                            : t("publicMenu:home.no_products_in_category")}
                        </div>
                      ) : (
                        visibleProducts.map((product) => (
                          <MenuCard
                            key={product.id}
                            product={product}
                            onAddToCart={onAddProduct}
                            onViewDetails={onAddProduct}
                            mode={orderMode}
                            isOpen={!!servicePointSelfService || isDeliveryAcceptingOrders}
                            theme={publicMenuTheme}
                            canOrderDirectly={
                              servicePointSelfService?.canCreateOrders ?? orderMode === EOrderPlaceType.Delivery
                            }
                          />
                        ))
                      )}
                    </div>
                  </Animate>
                )}

                {totalProductsPages > 1 &&
                  activeCategory !== AWARDS_TAB_ID &&
                  activeCategory !== PROMOTIONS_TAB_ID &&
                  activeCategory !== EXTRA_SERVICES_TAB_ID && (
                    <nav
                      className="flex flex-wrap items-center justify-center gap-3 py-6"
                      aria-label={t("list:pagination_aria_label")}
                    >
                      <button
                        type="button"
                        disabled={currentProductsPage === 1}
                        onClick={() => changeProductsPage(currentProductsPage - 1)}
                        className={`rounded-xl border px-4 py-2 text-sm font-bold shadow-sm transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40 ${
                          isLegacyTheme
                            ? "border-gray-200 bg-white text-gray-700 hover:border-orange-300 hover:text-orange-600"
                            : ""
                        }`}
                        style={
                          isLegacyTheme
                            ? undefined
                            : {
                                backgroundColor: publicMenuTheme.surfaceColor,
                                borderColor: publicMenuTheme.borderColor,
                                borderWidth: publicMenuTheme.borderThickness,
                                color: publicMenuTheme.textColor,
                              }
                        }
                        data-interaction-description-key="interactionDescriptions:publicMenu.pages.publicMenuStartPage.button.changeProductsPageCurrentProductsPage12"
                      >
                        {t("list:pagination_previous")}
                      </button>

                      <span
                        className={`text-sm font-semibold ${isLegacyTheme ? "text-gray-600" : ""}`}
                        style={isLegacyTheme ? undefined : { color: publicMenuTheme.textColor }}
                        aria-live="polite"
                      >
                        {t("list:pagination_page")} <strong>{currentProductsPage}</strong>{" "}
                        {t("list:pagination_of", { totalPages: totalProductsPages })}
                      </span>

                      <button
                        type="button"
                        disabled={currentProductsPage === totalProductsPages}
                        onClick={() => changeProductsPage(currentProductsPage + 1)}
                        className={`rounded-xl border px-4 py-2 text-sm font-bold shadow-sm transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40 ${
                          isLegacyTheme
                            ? "border-gray-200 bg-white text-gray-700 hover:border-orange-300 hover:text-orange-600"
                            : ""
                        }`}
                        style={
                          isLegacyTheme
                            ? undefined
                            : {
                                backgroundColor: publicMenuTheme.surfaceColor,
                                borderColor: publicMenuTheme.borderColor,
                                borderWidth: publicMenuTheme.borderThickness,
                                color: publicMenuTheme.textColor,
                              }
                        }
                        data-interaction-description-key="interactionDescriptions:publicMenu.pages.publicMenuStartPage.button.openMenu"
                      >
                        {t("list:pagination_next")}
                      </button>
                    </nav>
                  )}
              </div>
            </>
          )}
        </section>

        {currentView === EAppView.OrderTracking && <PedidoView setCurrentView={setCurrentView} mode={orderMode} />}

        {currentView === EAppView.OrderHistory && <OrderHistoryView setCurrentView={setCurrentView} />}

        {currentView === EAppView.CustomerMenu && !servicePointSelfService && <PublicMenuMarketingFooter />}
      </main>

      {!servicePointSelfService && (
        <SocialSharePupop
          isOpen={isSocialOpen}
          storeSlug={storeSlug}
          storeName={storeName}
          storeDescription={storeDescription}
          onClose={() => setIsSocialOpen(false)}
        />
      )}

      <ShoppingCartButton
        orderMode={orderMode}
        cart={cart}
        currentView={currentView}
        summary={summary}
        setIsCartOpen={setIsCartOpen}
        theme={publicMenuTheme}
        servicePointSelfService={!!servicePointSelfService}
        orderingEnabled={servicePointSelfService?.canCreateOrders ?? true}
      />

      {isCartOpen && (
        <ShoppingCartPupop
          allOrders={allOrders}
          setAllOrders={setAllOrders}
          setCart={setCart}
          cart={cart}
          setCurrentView={(e) => setCurrentView(e)}
          onClose={() => setIsCartOpen(false)}
          servicePointSelfService={servicePointSelfService}
        />
      )}

      {selectedProductForAddOns && (
        <AddProductRequestPupop
          product={selectedProductForAddOns}
          onClose={() => setSelectedProductForAddOns(null)}
          onConfirm={addToCart}
          theme={publicMenuTheme}
          confirmLabel={orderMode === EOrderPlaceType.Delivery ? undefined : t("publicMenu:menu_card.ask_attendant")}
          disableConfirm={orderMode !== EOrderPlaceType.Delivery}
        />
      )}

      {selectedNotice && (
        <NoticeDetailsPupop notice={selectedNotice} theme={publicMenuTheme} onClose={() => setSelectedNotice(null)} />
      )}
    </div>
  )
}
